import { Switch, Notification, Text } from "@mantine/core";
import { useState } from "react";
import "./ReminderSettings.scss";
import NotificationToggles from "@/components/NotificationToggles/NotificationToggles";

interface ReminderSettingsProps {
  settings: any;
  setSettings: (settings: any) => void;
  updateNotificationPreferences: (settings: any) => any;
  ability: any;
}

const ReminderSettings = ({
  settings,
  setSettings,
  updateNotificationPreferences,
  ability,
}: ReminderSettingsProps) => {
  const [notification, setNotification] = useState<{
    title: string;
    message: string;
    color: string;
  } | null>(null);

  const canViewClientEmailNotifications = ability.can(
    "view",
    "ClientEmailNotificationPreferences"
  );

  const handleToggle = async (
    category: string,
    key: string,
    value: boolean
  ) => {
    const updatedSettings = { ...settings, [key]: value };

    if (category === "clientEmailNotifications") {
      if (value) {
        updatedSettings.statuses.push(key);
      } else {
        updatedSettings.statuses = updatedSettings.statuses.filter(
          (status: string) => status !== key
        );
      }
    }

    setSettings(updatedSettings);

    try {
      await updateNotificationPreferences(updatedSettings);
    } catch (error) {
      console.error("Error updating data:", error);
      setNotification({
        title: "Error",
        message:
          "There was an error updating your settings. Please try again later.",
        color: "red",
      });
    }
  };

  // Loading state

  if (!settings) {
    return <div>Loading...</div>; // Add a loading state
  }

  return (
    <div className="reminder-settings">
      {notification && (
        <Notification
          title={notification.title}
          color={notification.color}
          onClose={() => setNotification(null)}
          withCloseButton
        >
          {notification.message}
        </Notification>
      )}

      <div className="sections-container">
        <div className="section">
          <div className="section-header">
            <Text mt="lg" fw={500}>
              Email Notification Preferences
            </Text>
            <Text size="sm" mb="lg">
              You will receive an email notification on the following events:
            </Text>
          </div>
          <ul className="email-preferences">
            <li className="email-preference-item email-item">
              <Text>Newly created clients</Text>

              <Switch
                size="md"
                checked={settings?.newClients}
                onChange={(event) =>
                  handleToggle(
                    "newClients",
                    "newClients",
                    event.currentTarget.checked
                  )
                }
              />
            </li>
            <li className="email-preference-item email-item">
              <Text>Clients who complete their tax organizer</Text>
              <Switch
                size="md"
                checked={settings?.clientFinishedOrganizer}
                onChange={(event) =>
                  handleToggle(
                    "clientFinishedOrganizer",
                    "clientFinishedOrganizer",
                    event.currentTarget.checked
                  )
                }
              />
            </li>
            <li className="email-preference-item email-item">
              <Text>Clients who upload documents</Text>
              <Switch
                size="md"
                checked={settings?.clientUploadedDocs}
                onChange={(event) =>
                  handleToggle(
                    "clientUploadedDocs",
                    "clientUploadedDocs",
                    event.currentTarget.checked
                  )
                }
              />
            </li>
          </ul>
        </div>
        {canViewClientEmailNotifications && (
          <div className="section">
            <div className="section-header">
              <Text mt="lg" fw={500}>
                Client Email Notification Preferences
              </Text>
              <Text size="sm" mb="lg">
                Your clients will receive email notifications on the following
                statuses:
              </Text>
            </div>
            {/* Client Email Notification Preferences */}
            <NotificationToggles
              settings={settings}
              handleToggle={handleToggle}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReminderSettings;
